/**
 * Convergence point axes - a small world-aligned X/Y/Z axis triad fixed at
 * the stereo rig's convergence point as it was when G was pressed
 * (2026-10-23). G shows it there; the next G hides it.
 *
 * The eyes are parallel with an off-axis projection shift (camera.ts
 * eyeProjection), so the convergence point is simply the eyes' center
 * (the center camera's position) plus its forward axis times
 * splitConvergenceDistance - the spot that lands on the same pixel in both
 * eyes. A triad that FOLLOWED it would always sit at the screen center, on
 * the screen surface, whatever the view or distance. Fixed in the world
 * instead, it behaves like a scene object: rotating the view moves it off
 * center, raising Convergence Dist brings it out of the screen, lowering
 * it sends it behind.
 *
 * Drawn in the World layer, the only layer the viewer-mode main pass
 * renders (and the one it renders once per eye in split view), as a
 * transparent draw that ignores depth and sorts after everything else -
 * so the splats never hide it - and is never cut by the far clip plane.
 * Its size is set at the press (a fraction of the distance then). Shown
 * only in stereo split view; a placed triad stays placed while stereo is
 * off.
 */

import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    PRIMITIVE_TRIANGLES,
    Entity,
    Mesh,
    MeshInstance,
    ShaderMaterial,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from '../element';

const vertexShader = /* glsl */ `
    attribute vec3 vertex_position;
    attribute vec4 vertex_color;

    varying vec4 vColor;

    uniform mat4 matrix_model;
    uniform mat4 matrix_viewProjection;

    void main(void) {
        gl_Position = matrix_viewProjection * matrix_model * vec4(vertex_position, 1.0);
        vColor = vertex_color;

        // no depth clipping: the camera's far plane hugs the scene bound, so
        // a convergence point past the scene would be cut away (seen from
        // ~10.5 m on makeup-v3). Depth is never tested here, so pinning it
        // inside the clip volume changes nothing else.
        gl_Position.z = 0.0;
    }
`;

const fragmentShader = /* glsl */ `
    precision highp float;

    varying vec4 vColor;

    void main(void) {
        gl_FragColor = vColor;
    }
`;

/** axis length as a fraction of the convergence distance */
const AXIS_LENGTH_RATIO = 0.1;
/** bar thickness as a fraction of the axis length */
const AXIS_THICKNESS_RATIO = 0.025;

// unit-length triad: one square bar per axis direction (+ bright, - dark)
// plus a white center cube
const buildGeometry = () => {
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    // axis-aligned box from min to max, one color
    const pushBox = (min: number[], max: number[], rgb: number[]) => {
        const base = positions.length / 3;
        for (let i = 0; i < 8; i++) {
            positions.push(i & 1 ? max[0] : min[0], i & 2 ? max[1] : min[1], i & 4 ? max[2] : min[2]);
            colors.push(rgb[0], rgb[1], rgb[2], 255);
        }
        // 6 faces as corner-index quads
        const faces = [[0, 1, 3, 2], [4, 6, 7, 5], [0, 4, 5, 1], [2, 3, 7, 6], [0, 2, 6, 4], [1, 5, 7, 3]];
        for (const [a, b, c, d] of faces) {
            indices.push(base + a, base + b, base + c, base + a, base + c, base + d);
        }
    };

    const t = AXIS_THICKNESS_RATIO / 2;
    pushBox([0, -t, -t], [1, t, t], [255, 60, 60]);     // +X - red
    pushBox([-t, 0, -t], [t, 1, t], [60, 220, 60]);     // +Y - green
    pushBox([-t, -t, 0], [t, t, 1], [70, 120, 255]);    // +Z - blue
    // negative halves: same hue, darker, so the sign still reads
    pushBox([-1, -t, -t], [0, t, t], [110, 25, 25]);    // -X
    pushBox([-t, -1, -t], [t, 0, t], [25, 95, 25]);     // -Y
    pushBox([-t, -t, -1], [t, t, 0], [30, 50, 120]);    // -Z
    const c = t * 2;
    pushBox([-c, -c, -c], [c, c, c], [255, 255, 255]);  // the point itself

    return { positions, colors, indices };
};

const point = new Vec3();

class FtgsConvergenceAxes extends Element {
    private entity: Entity;
    private _visible = false;
    /** the next frame with a convergence point places the triad there */
    private placePending = false;
    private placed = false;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const { scene } = this;

        const material = new ShaderMaterial({
            uniqueName: 'ftgsConvergenceAxesMaterial',
            vertexGLSL: vertexShader,
            fragmentGLSL: fragmentShader
        });
        // transparent (so it sorts with - and after - the splats), but
        // fully opaque colors; never hidden by depth
        material.blendType = BLEND_NORMAL;
        material.depthTest = false;
        material.depthWrite = false;
        material.cull = CULLFACE_NONE;
        material.update();

        const { positions, colors, indices } = buildGeometry();
        const mesh = new Mesh(scene.graphicsDevice);
        mesh.setPositions(positions);
        mesh.setColors32(new Uint8Array(colors));
        mesh.setIndices(indices);
        mesh.update(PRIMITIVE_TRIANGLES);

        const meshInstance = new MeshInstance(mesh, material);
        meshInstance.cull = false;
        // the World layer sorts transparent draws back to front (largest
        // key first): a huge negative distance puts this one last
        meshInstance.calculateSortDistance = () => -1e8;

        this.entity = new Entity('ftgsConvergenceAxes');
        this.entity.addComponent('render', {
            meshInstances: [meshInstance],
            layers: [scene.worldLayer.id]
        });
        this.entity.enabled = false;
        scene.app.root.addChild(this.entity);
    }

    destroy() {
        this.entity?.destroy();
        super.destroy();
    }

    /** G: show the triad fixed at the current convergence point, or hide it */
    toggle() {
        this._visible = !this._visible;
        this.placePending = this._visible;
        this.scene.forceRender = true;
    }

    get visible() {
        return this._visible;
    }

    /** placed (G) AND stereo split view is on - only then is anything shown */
    get active() {
        return this._visible && this.scene.camera.splitEnabled;
    }

    onPreRender() {
        const { camera } = this.scene;
        const distance = camera.splitConvergenceDistance;

        // fixed where the convergence point was at the G press: the eyes'
        // center (camera.onUpdate has already settled this frame's pose)
        // plus Convergence Dist along the view direction. It then stays
        // put in the world - rotating the view moves it off center, a
        // Convergence Dist change brings it out of / behind the screen. A
        // parallel rig (distance 0) has its point at infinity: the place
        // waits for a real distance.
        if (this.placePending && distance > 0) {
            this.placePending = false;
            this.placed = true;
            const main = camera.mainCamera;
            this.entity.setPosition(point.copy(main.forward).mulScalar(distance).add(main.getPosition()));
            // sized once, from the distance at the press: a fixed world
            // size shrinks with distance like the scene around it
            const length = distance * AXIS_LENGTH_RATIO;
            this.entity.setLocalScale(length, length, length);
        }

        // stereo view only (user request 2026-10-23): with one center view
        // there is no parallax to show
        this.entity.enabled = this.active && this.placed && camera.renderOverlays;
    }
}

export { FtgsConvergenceAxes };
